import React, { useCallback } from 'react';

import noop from 'lodash/noop';

import {
  FileFormat,
  useReportsPrintDashboardGet,
} from '@sbv-clients/data-access-api';
import {
  ALL_OPTIONS_VALUE,
  DateFilter,
  DepartmentFilter,
  PositionFilter,
  withSanitiseFilterIdValue,
} from '@sbv-clients/domain-ui-shared';
import { useAuth } from '@sbv-clients/shared-feature-auth';
import { Box, Button, Icon, Typography, Tooltip } from '@sbv-clients/ui-shared';
import { formatDate } from '@sbv-clients/util-misc';

import { Page, useContextFn } from './context/page';
import { DashboardContextType } from './context/page-context';
import Main from './main';

const ActionButton = ({
  handleExport,
  isLoading,
  useContextFn,
}: {
  handleExport: () => void;
  isLoading: boolean;
  useContextFn: () => DashboardContextType;
}) => {
  const { departmentId, positionId } = useContextFn();
  const renderButton = useCallback(
    (disabled?: boolean) => {
      return (
        <Button
          variant="contained"
          size="small"
          onClick={handleExport}
          disabled={isLoading || disabled}
        >
          <Typography mr={2}>Export </Typography> <Icon name={'East'} />
        </Button>
      );
    },
    [handleExport, isLoading]
  );
  if (departmentId === ALL_OPTIONS_VALUE || positionId === ALL_OPTIONS_VALUE) {
    return (
      <Tooltip title="'All' option not supported for exports" arrow>
        <span>{renderButton(true)}</span>
      </Tooltip>
    );
  }
  return renderButton(false);
};

const Header = () => {
  const { date, departmentId, positionId } = useContextFn();

  const reportDashboardGet = useReportsPrintDashboardGet();
  const handleExport = useCallback(async () => {
    await reportDashboardGet
      .mutateAsync({
        date: formatDate(new Date(date as any), 'yyyy-MM-dd'),
        positionId: withSanitiseFilterIdValue(positionId),
        departmentId: withSanitiseFilterIdValue(departmentId),
        format: FileFormat.NUMBER_1,
      })
      .then(noop)
      .catch(noop);
  }, [date, departmentId, positionId, reportDashboardGet]);

  return (
    <Box
      display="grid"
      alignItems="flex-end"
      gap={2}
      sx={{
        gridTemplateColumns: {
          xs: '1fr',
          md: 'auto 1fr 150px 150px 150px auto',
        },
      }}
    >
      <Typography variant="h3">My Dashboard</Typography>
      <Box />
      <DateFilter label="Date" useContextFn={useContextFn} />
      <DepartmentFilter
        useContextFn={useContextFn}
        autoSelectIfEmpty
        withAllOption
      />
      <PositionFilter
        useContextFn={useContextFn}
        autoSelectIfEmpty
        withAllOption
      />
      <ActionButton
        handleExport={handleExport}
        isLoading={reportDashboardGet.isLoading}
        useContextFn={useContextFn}
      />
    </Box>
  );
};

export const PortalDashboardPage = () => {
  const { isMultiFacility } = useAuth();

  if (isMultiFacility) {
    return (
      <Box p={4}>
        <Typography variant="h3">My Dashboard</Typography>
        <Typography mt={2}>
          Workforce dashboard view is not currently supported for multi-facility
          applications
        </Typography>
      </Box>
    );
  }

  return (
    <Page>
      <Box py={2} px={4} bgcolor="white" height="100%" overflow="auto">
        <Header />
        <Main />
      </Box>
    </Page>
  );
};
